import { useState } from "react";
import { useParams, useNavigate } from "react-router-dom";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { Avatar, AvatarFallback } from "@/components/ui/avatar";
import { Separator } from "@/components/ui/separator";
import { ArrowLeft, Edit, Calendar, User, Building2, FileText, CheckCircle } from "lucide-react";
import { useComplianceTask, useDepartments } from "@/hooks";
import { useAuth } from "@/contexts/AuthContext";
import { TaskStatusBadge } from "@/components/compliance/TaskStatusBadge";
import { PriorityIndicator } from "@/components/compliance/PriorityIndicator";
import { TaskFormModal } from "@/components/compliance/TaskFormModal";
import { TaskComments } from "@/components/compliance/TaskComments";
import type { ComplianceTask } from "@/hooks/use-compliance-tasks";

export default function TaskDetail() {
  const { id } = useParams<{ id: string }>();
  const navigate = useNavigate();
  const { profile } = useAuth();
  const { task, loading, error, updateTask, completeTask } = useComplianceTask(id);
  const { departments } = useDepartments();
  const [isFormModalOpen, setIsFormModalOpen] = useState(false);
  const [isCompleting, setIsCompleting] = useState(false);

  if (id === "new") {
    navigate("/compliance/tasks");
    return null;
  }

  const getInitials = (name: string) =>
    name.split(" ").map((n) => n[0]).join("").toUpperCase().slice(0, 2);

  const getDepartmentName = (departmentId: string | null) => {
    if (!departmentId) return "General";
    return departments?.find((d) => d.id === departmentId)?.name || "General";
  };

  const getDepartmentColor = (departmentId: string | null) => {
    if (!departmentId) return "#6366f1";
    return departments?.find((d) => d.id === departmentId)?.color || "#6366f1";
  };

  const isOverdue = (dueDate: string | null, status: string) => {
    if (!dueDate || status === "complete") return false;
    return new Date(dueDate) < new Date();
  };

  const handleEdit = () => setIsFormModalOpen(true);

  const handleSave = async (taskData: Partial<ComplianceTask>) => {
    if (!id) return;
    await updateTask(id, taskData);
    setIsFormModalOpen(false);
  };

  const handleComplete = async () => {
    if (!id || !profile?.id) return;
    setIsCompleting(true);
    try {
      await completeTask(id, profile.id);
    } finally {
      setIsCompleting(false);
    }
  };

  if (loading) {
    return (
      <div className="space-y-6">
        <div className="flex items-center gap-2">
          <Button variant="ghost" size="icon" onClick={() => navigate("/compliance/tasks")}>
            <ArrowLeft className="h-4 w-4" />
          </Button>
          <div className="animate-pulse">
            <div className="h-8 bg-gray-200 rounded w-64"></div>
          </div>
        </div>
        <Card>
          <CardContent className="p-6">
            <div className="animate-pulse space-y-2">
              <div className="h-4 bg-gray-200 rounded w-3/4"></div>
              <div className="h-4 bg-gray-200 rounded w-1/2"></div>
            </div>
          </CardContent>
        </Card>
      </div>
    );
  }

  if (error || !task) {
    return (
      <div className="space-y-6">
        <div className="flex items-center gap-2">
          <Button variant="ghost" size="icon" onClick={() => navigate("/compliance/tasks")}>
            <ArrowLeft className="h-4 w-4" />
          </Button>
        </div>
        <Card>
          <CardContent className="p-6">
            <p className="text-destructive">{error || "Task not found"}</p>
          </CardContent>
        </Card>
      </div>
    );
  }

  const overdue = isOverdue(task.due_date, task.status);

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div className="flex items-center gap-4">
          <Button variant="ghost" size="icon" onClick={() => navigate("/compliance/tasks")}>
            <ArrowLeft className="h-4 w-4" />
          </Button>
          <div>
            <div className="flex items-center gap-3">
              <h1 className="text-3xl font-medium text-foreground">{task.title}</h1>
              <Button variant="ghost" size="sm" onClick={handleEdit}>
                <Edit className="h-4 w-4 mr-2" />
                Edit
              </Button>
            </div>
            <div className="flex items-center gap-2 mt-2">
              <PriorityIndicator priority={task.priority} />
              <TaskStatusBadge status={task.status} />
              <Badge
                variant="outline"
                style={{
                  backgroundColor: getDepartmentColor(task.department_id) + "15",
                  borderColor: getDepartmentColor(task.department_id),
                }}
              >
                {getDepartmentName(task.department_id)}
              </Badge>
            </div>
          </div>
        </div>
      </div>

      <div className="grid gap-6 md:grid-cols-3">
        {/* Main Content */}
        <div className="md:col-span-2 space-y-6">
          {/* Description */}
          <Card>
            <CardHeader>
              <CardTitle>Description</CardTitle>
            </CardHeader>
            <CardContent>
              <p className="text-muted-foreground whitespace-pre-wrap">
                {task.description || "No description provided"}
              </p>
            </CardContent>
          </Card>

          {/* Comments */}
          <Card>
            <CardHeader>
              <CardTitle>Comments</CardTitle>
            </CardHeader>
            <CardContent>
              <TaskComments taskId={task.id} />
            </CardContent>
          </Card>
        </div>

        {/* Sidebar */}
        <div className="space-y-6">
          {/* Actions */}
          {task.status !== "complete" && (
            <Card>
              <CardHeader>
                <CardTitle>Actions</CardTitle>
              </CardHeader>
              <CardContent>
                <Button className="w-full" disabled={isCompleting} onClick={handleComplete}>
                  <CheckCircle className="h-4 w-4 mr-2" />
                  {isCompleting ? "Completing…" : "Mark as Complete"}
                </Button>
              </CardContent>
            </Card>
          )}

          {/* Details */}
          <Card>
            <CardHeader>
              <CardTitle>Details</CardTitle>
            </CardHeader>
            <CardContent className="space-y-4">
              <div className="flex items-start gap-3">
                <User className="h-5 w-5 text-muted-foreground mt-0.5" />
                <div className="flex-1">
                  <p className="text-sm font-medium mb-1">Assignee</p>
                  {task.primary_assignee ? (
                    <div className="flex items-center gap-2">
                      <Avatar className="h-6 w-6">
                        <AvatarFallback className="text-xs">
                          {getInitials(task.primary_assignee.name)}
                        </AvatarFallback>
                      </Avatar>
                      <span className="text-sm">{task.primary_assignee.name}</span>
                    </div>
                  ) : (
                    <p className="text-sm text-muted-foreground">Unassigned</p>
                  )}
                </div>
              </div>

              <Separator />

              <div className="flex items-start gap-3">
                <Calendar className="h-5 w-5 text-muted-foreground mt-0.5" />
                <div className="flex-1">
                  <p className="text-sm font-medium mb-1">Due Date</p>
                  {task.due_date ? (
                    <p className={`text-sm ${overdue ? "text-destructive" : ""}`}>
                      {new Date(task.due_date).toLocaleDateString()}
                      {overdue && <span className="ml-2 text-xs">(Overdue)</span>}
                    </p>
                  ) : (
                    <p className="text-sm text-muted-foreground">No due date</p>
                  )}
                </div>
              </div>

              <Separator />

              <div className="flex items-start gap-3">
                <Building2 className="h-5 w-5 text-muted-foreground mt-0.5" />
                <div className="flex-1">
                  <p className="text-sm font-medium mb-1">Department</p>
                  <p className="text-sm">{getDepartmentName(task.department_id)}</p>
                </div>
              </div>

              {task.policy && (
                <>
                  <Separator />
                  <div className="flex items-start gap-3">
                    <FileText className="h-5 w-5 text-muted-foreground mt-0.5" />
                    <div className="flex-1">
                      <p className="text-sm font-medium mb-1">Related Policy</p>
                      <p className="text-sm">{task.policy.title}</p>
                    </div>
                  </div>
                </>
              )}
            </CardContent>
          </Card>

          {/* Timeline */}
          <Card>
            <CardHeader>
              <CardTitle>Activity</CardTitle>
            </CardHeader>
            <CardContent>
              <div className="space-y-4">
                <div>
                  <p className="text-sm font-medium">Created</p>
                  <p className="text-xs text-muted-foreground">
                    {new Date(task.created_at).toLocaleDateString()}
                  </p>
                </div>
                {task.completed_at && (
                  <>
                    <Separator />
                    <div>
                      <p className="text-sm font-medium">Completed</p>
                      <p className="text-xs text-muted-foreground">
                        {new Date(task.completed_at).toLocaleDateString()}
                      </p>
                    </div>
                  </>
                )}
              </div>
            </CardContent>
          </Card>
        </div>
      </div>

      <TaskFormModal
        open={isFormModalOpen}
        onOpenChange={setIsFormModalOpen}
        task={task}
        onSave={handleSave}
      />
    </div>
  );
}
